import { Link, useParams } from "react-router-dom";
import { Share2 } from "lucide-react";
import PageTransition from "../components/PageTransition";
import ScrollReveal from "../components/ScrollReveal";
import SuccessCheckmark from "../components/SuccessCheckmark";
import Breadcrumbs from "../components/Breadcrumbs";
import useDocumentTitle from "../hooks/useDocumentTitle";
import useCopyToClipboard from "../hooks/useCopyToClipboard";
import { CASE_STUDIES } from "../data/caseStudies";

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const caseStudy = CASE_STUDIES.find((cs) => cs.slug === slug);
  useDocumentTitle(caseStudy ? caseStudy.title : "Case Study");
  const { copy, copied } = useCopyToClipboard();

  if (!caseStudy) {
    return (
      <PageTransition>
        <section className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center">
          <p className="text-ocean-800/60">This case study isn&apos;t available.</p>
          <Link to="/success-stories" className="mt-4 text-sm font-semibold text-teal-600 hover:text-teal-700">
            Back to Success Stories
          </Link>
        </section>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      {/* pt-32 sm:pt-40 clears Navbar's fixed header — same effective offset
          every other page gets from its hero section's own top padding.
          This page has no hero, so Breadcrumbs needs it applied directly. */}
      <div className="pt-32 sm:pt-40">
        <Breadcrumbs dynamicLabel={caseStudy.title} />
      </div>

      <section className="mx-auto max-w-3xl px-6 pt-10 pb-32">
        <ScrollReveal>
          <h1 className="font-display text-3xl font-bold text-ocean-950 sm:text-4xl">{caseStudy.title}</h1>
          {/* AC asks for a shareable link with an Open Graph preview card. This
              is a client-only SPA (see index.html, jira/ZP-78) with no SSR/
              prerendering, so a crawler-visible per-page OG card isn't
              achievable here — sharing a plain link is the honest fallback. */}
          <button
            type="button"
            onClick={() => copy(window.location.href)}
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-600 hover:text-teal-700"
          >
            {copied ? <SuccessCheckmark className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
            {copied ? "Link copied" : "Share"}
          </button>
        </ScrollReveal>

        <ScrollReveal delay={0.1} className="mt-10">
          <h2 className="font-display text-lg font-semibold text-ocean-950">Challenge</h2>
          <p className="mt-2 leading-relaxed text-ocean-800/70">{caseStudy.challenge}</p>
        </ScrollReveal>

        <ScrollReveal delay={0.15} className="mt-8">
          <h2 className="font-display text-lg font-semibold text-ocean-950">ZenFresh Solution</h2>
          <p className="mt-2 leading-relaxed text-ocean-800/70">{caseStudy.solution}</p>
        </ScrollReveal>

        <ScrollReveal delay={0.2} className="mt-8">
          <h2 className="font-display text-lg font-semibold text-ocean-950">Measurable Outcome</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            {caseStudy.outcomeStats.map((stat) => (
              <div key={stat.label} className="rounded-2xl bg-teal-400/10 p-5 text-center">
                <p className="font-display text-2xl font-bold text-teal-700">{stat.value}</p>
                <p className="mt-1 text-xs text-ocean-800/60">{stat.label}</p>
              </div>
            ))}
          </div>
        </ScrollReveal>
      </section>
    </PageTransition>
  );
}
